import { useLingui } from '@lingui/react';
import {
  AlertTriangle,
  Baby,
  Clock,
  GraduationCap,
  Palmtree,
  Thermometer,
  User,
  type LucideIcon,
} from 'lucide-react';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import type { AbsenceTypeValue } from '@/types/employeeAbsences';
import { AbsenceType } from '@/types/employeeAbsences';

interface AbsenceTypeBadgeProps {
  /**
   * The wire types expose this as a free `string` (#1156), so the prop takes
   * one too; any value outside the known set renders as a neutral badge with
   * the raw value.
   */
  type: AbsenceTypeValue | (string & {});
  className?: string;
}

/**
 * Tone and icon per absence type. The icon tells the types apart, so two
 * types may share a tone (sick and child sick are both sickness reports).
 */
export const ABSENCE_TYPE_TONES: Record<AbsenceTypeValue, { tone: StatusTone; icon?: LucideIcon }> = {
  [AbsenceType.VACATION]: { tone: 'info', icon: Palmtree },
  [AbsenceType.SICK]: { tone: 'danger', icon: Thermometer },
  [AbsenceType.CHILD_SICK]: { tone: 'danger', icon: Baby },
  [AbsenceType.PERSONAL]: { tone: 'accent', icon: User },
  [AbsenceType.TRAINING]: { tone: 'success', icon: GraduationCap },
  [AbsenceType.ACCIDENT]: { tone: 'warning', icon: AlertTriangle },
  [AbsenceType.COMP_TIME]: { tone: 'neutral', icon: Clock },
  [AbsenceType.OTHER]: { tone: 'neutral' },
};

export function getAbsenceTypeLabel(
  // Accepts any wire value; the `|| type` fallback below already returned the
  // raw string for anything outside the known set.
  type: AbsenceTypeValue | (string & {}),
  i18n: { _: (descriptor: { id: string; message: string }) => string },
): string {
  const labels: Partial<Record<AbsenceTypeValue | (string & {}), string>> = {
    [AbsenceType.VACATION]: i18n._({
      id: 'absences.type.vacation',
      message: 'Vacation',
    }),
    [AbsenceType.SICK]: i18n._({
      id: 'absences.type.sick',
      message: 'Sick',
    }),
    [AbsenceType.PERSONAL]: i18n._({
      id: 'absences.type.personal',
      message: 'Personal',
    }),
    [AbsenceType.TRAINING]: i18n._({
      id: 'absences.type.training',
      message: 'Training',
    }),
    [AbsenceType.ACCIDENT]: i18n._({
      id: 'absences.type.accident',
      message: 'Accident',
    }),
    [AbsenceType.COMP_TIME]: i18n._({
      id: 'absences.type.compTime',
      message: 'Comp time',
    }),
    [AbsenceType.CHILD_SICK]: i18n._({
      id: 'absences.type.childSick',
      message: 'Child sick',
    }),
    [AbsenceType.OTHER]: i18n._({
      id: 'absences.type.other',
      message: 'Other',
    }),
  };
  return labels[type] || type;
}

export function AbsenceTypeBadge({ type, className }: AbsenceTypeBadgeProps) {
  const { i18n } = useLingui();
  const config = ABSENCE_TYPE_TONES[type as AbsenceTypeValue] ?? { tone: 'neutral' as const };

  return (
    <StatusBadge
      tone={config.tone}
      icon={config.icon}
      label={getAbsenceTypeLabel(type, i18n)}
      className={className}
    />
  );
}
